import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useAppDispatch } from '../../../../../features';
import { openModal } from '../../../../../features/slice/event/event-slice';
import type { ProductDto } from '../../../../../features/slice/products/product/product.schema';
import { useCreateNewProductMutation } from '../../../../../features/slice/products/product/productApiSlice';
import useFetchFieldOptions from '../../../../../hooks/use-fetch-field-options';
import type { FieldOptions } from '../../../../../types/dynamic-control-types';
import { createProduct } from '../utils/product-inputs';
import { CreateProductSchema } from './create-product.schema';

export const useCreateProduct = () => {
    const createProductform = useForm<ProductDto>({
        resolver: zodResolver(CreateProductSchema),
        defaultValues: {
            name: '',
            brand_id: '',
            generic_id: '',
            shelf: '',
            variations: [
                {
                    type_id: '',
                    unit_id: '',
                    strength_id: '',
                    purchase_price: 0,
                    wholesale_price: 0,
                    retail_price: 0,
                },
            ],
        },
    });

    const dispatch = useAppDispatch();
    const onClickFormActionBtn = (clickItem = '') => {
        dispatch(
            openModal({
                content: clickItem,
            }),
        );
    };
    const [createNewProduct, { isLoading }] = useCreateNewProductMutation();
    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = createProductform;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'variations',
    });

    const handleProductSubmit = async (value: ProductDto) => {
        try {
            const { data } = await createNewProduct({
                name: value.name,
                brand_id: value.brand_id,
                generic_id: value.generic_id,
                shelf: value.shelf,
                variations: value.variations.map((variation: any) => {
                    return {
                        type_id: variation.type_id,
                        unit_id: variation.unit_id,
                        strength_id: variation.strength_id,
                        purchase_price: variation.purchase_price,
                        wholesale_price: variation.wholesale_price,
                        retail_price: variation.retail_price,
                    };
                }),
            }).unwrap();

            if (data.name) {
                reset();
                toast.success('A New Product has been listed.', {
                    style: {
                        background: 'green',
                        color: 'white',
                    },
                });
            } else {
                toast.success(data.message, {
                    style: {
                        background: 'red',
                        color: 'white',
                    },
                });
            }
        } catch (error) {
            if (error) {
                toast.success((error as any).data.message, {
                    style: {
                        background: 'red',
                        color: 'white',
                    },
                });
            } else {
                console.error('An unknown error occurred');
            }
        }
    };

    // const fieldOptionsForProductInfo: FieldOptions =
    //   createProduct.productInformation.fields.reduce((acc, field) => {
    //     // eslint-disable-next-line
    //     acc[field.fieldName] = useFetchFieldOptions(field.fieldName);
    //     return acc;
    //   }, {} as FieldOptions);

    const fieldOptionsForPricing: FieldOptions =
        createProduct.productPricing.fields.reduce((acc, field) => {
            // biome-ignore lint/correctness/useHookAtTopLevel: TODO: It needs to be refactored
            // eslint-disable-next-line
            acc[field.fieldName] = useFetchFieldOptions(field.fieldName);
            return acc;
        }, {} as FieldOptions);
    return {
        createProductform,
        isLoading,
        handleSubmit,
        fields,
        append,
        remove,
        onClickFormActionBtn,
        isValid,
        control,
        //fieldOptionsForProductInfo,
        fieldOptionsForPricing,
        handleProductSubmit,
    };
};
